import React, { Suspense } from 'react';

// Performance-optimized lazy loading with error boundaries
export const createLazyComponent = (importFunc, fallbackComponent, options = {}) => {
  const {
    loadingComponent = null,
    errorFallback = null,
    preload = false
  } = options;
  
  const defaultLoadingComponent = loadingComponent || <div>Loading...</div>;
  
  // Create lazy component with error handling
  const LazyComponent = React.lazy(() => 
    importFunc().catch(error => {
      console.error('Lazy component failed to load:', error);
      
      // Return fallback component as default export
      return {
        default: fallbackComponent || (() => (
          <div className="p-4 text-center text-red-600">
            Failed to load component. Please refresh the page.
          </div>
        ))
      };
    })
  );
  
  // Preload component if requested
  if (preload && typeof window !== 'undefined') {
    // Preload after a short delay to not block initial render
    setTimeout(() => {
      importFunc().catch(() => {
        // Ignore preload failures
      });
    }, 100);
  }
  
  // Return wrapped component with Suspense
  const WrappedComponent = (props) => (
    <Suspense fallback={defaultLoadingComponent}>
      <LazyComponent {...props} />
    </Suspense>
  );
  
  // Expose preload method
  WrappedComponent.preload = () => importFunc();
  
  return WrappedComponent;
};

// Skeleton components for different page types
export const DashboardSkeleton = () => (
  <div className="dashboard-skeleton animate-pulse space-y-6">
    <div className="h-8 bg-stone-200 rounded mb-4"></div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="h-48 bg-stone-200 rounded"></div>
      <div className="h-48 bg-stone-200 rounded"></div>
    </div>
    <div className="h-32 bg-stone-200 rounded"></div>
  </div>
);

export const EventsSkeleton = () => (
  <div className="events-skeleton animate-pulse space-y-6">
    <div className="h-8 bg-stone-200 rounded mb-4"></div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="h-48 bg-stone-200 rounded"></div>
        <div className="h-32 bg-stone-200 rounded"></div>
      </div>
      <div className="space-y-4">
        <div className="h-32 bg-stone-200 rounded"></div>
        <div className="h-48 bg-stone-200 rounded"></div>
      </div>
    </div>
  </div>
);

export const MessagesSkeleton = () => (
  <div className="messages-skeleton animate-pulse space-y-4">
    <div className="h-8 bg-stone-200 rounded mb-4"></div>
    <div className="space-y-3">
      <div className="h-16 bg-stone-200 rounded"></div>
      <div className="h-16 bg-stone-200 rounded"></div>
      <div className="h-16 bg-stone-200 rounded"></div>
    </div>
  </div>
);

// Route-based code splitting configuration (define import paths as strings to avoid circular imports)
export const ROUTE_COMPONENTS = {
  dashboard: {
    componentPath: '../../../components/Dashboard/DashboardContainer',
    fallbackPath: '../../../components/Dashboard/LegacyParentDashboard',
    skeleton: DashboardSkeleton,
    preload: true // Critical route
  },
  events: {
    componentPath: '../../../components/Events/EventsContainer', 
    fallbackPath: '../../../components/Family/UnifiedEventsPageDesktop',
    skeleton: EventsSkeleton,
    preload: false
  },
  messages: {
    componentPath: '../../../components/Messages/FrictionlessFamilyMessages',
    fallbackPath: null,
    skeleton: MessagesSkeleton,
    preload: false
  }
};

// Performance monitoring for lazy loading
export class LazyLoadMonitor {
  constructor() {
    this.loadTimes = new Map();
    this.failures = new Map();
  }
  
  startLoad(componentName) {
    this.loadTimes.set(componentName, performance.now());
  }
  
  endLoad(componentName, success = true) {
    const startTime = this.loadTimes.get(componentName);
    if (startTime) {
      const duration = performance.now() - startTime;
      console.log(`Lazy load ${componentName}: ${duration.toFixed(2)}ms`);
      
      if (!success) {
        const failures = this.failures.get(componentName) || 0;
        this.failures.set(componentName, failures + 1);
        console.warn(`Lazy load failure for ${componentName} (${failures + 1} times)`);
      }
    }
  }
  
  getReport() {
    return {
      loadTimes: Object.fromEntries(this.loadTimes),
      failures: Object.fromEntries(this.failures)
    };
  }
}

export const lazyLoadMonitor = new LazyLoadMonitor();